import { useEffect, useState } from 'react';
import { api, ApiError, type Pin } from '../api';
import { useApp } from '../context';
import { ChangePassword } from '../screens/ChangePassword';

/** Account zonder Plex: wachtwoord wijzigen en (later) een Plex-account koppelen ("Beide"). */
export function LocalAccountSettings() {
  const { me, switchProfile } = useApp();
  const kind = me.account.kind;
  const [pw, setPw] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [link, setLink] = useState<{ linked: boolean; username: string | null } | null>(null);
  const [pin, setPin] = useState<Pin | null>(null);
  const main = me.profile?.admin !== false;

  useEffect(() => {
    if (kind === 'local' || kind === 'both') api.plexLink().then(setLink).catch(() => setLink(null));
  }, [kind]);

  // Wachten tot de code op plex.tv/link is bevestigd
  useEffect(() => {
    if (!pin) return;
    let stop = false;
    let t: number | undefined;
    const poll = async () => {
      if (stop) return;
      try {
        const r = await api.plexLinkCheck(pin.id);
        if (r.status === 'ok') {
          setPin(null);
          setMsg(r.message);
          setLink({ linked: true, username: null });
          // Profiel opnieuw kiezen: dan komt de bibliotheek erbij
          window.setTimeout(() => switchProfile(), 2500);
          return;
        }
      } catch (err) {
        setPin(null);
        setMsg(err instanceof ApiError && err.message !== err.code ? err.message : 'Koppelen mislukt.');
        return;
      }
      t = window.setTimeout(poll, 2000);
    };
    t = window.setTimeout(poll, 2000);
    return () => {
      stop = true;
      window.clearTimeout(t);
    };
  }, [pin, switchProfile]);

  if (kind !== 'local' && kind !== 'both') return null;

  return (
    <section className="settings-block" aria-label="Je account">
      <h2>Je account</h2>
      <p className="subtle hint">Je logt in met <strong>{me.account.username}</strong> en een wachtwoord.</p>
      {pw ? (
        <ChangePassword onDone={() => { setPw(false); setMsg('Wachtwoord gewijzigd.'); }} onCancel={() => setPw(false)} />
      ) : (
        <div className="form-actions"><button className="btn" onClick={() => setPw(true)}>Wachtwoord wijzigen</button></div>
      )}

      {main && (
        <>
          <h3 style={{ marginTop: 'calc(var(--u) * 2.4)' }}>Plex</h3>
          {link?.linked ? (
            <>
              <p className="subtle hint">Plex is gekoppeld{link.username ? ` (${link.username})` : ''}. Als je toegang hebt tot de bibliotheek van deze server, zie je die naast je addons.</p>
              <div className="form-actions">
                <button className="btn btn-ghost" onClick={async () => { await api.plexUnlink().catch(() => undefined); setLink({ linked: false, username: null }); setMsg('Plex ontkoppeld.'); window.setTimeout(() => switchProfile(), 1500); }}>Plex ontkoppelen</button>
              </div>
            </>
          ) : pin ? (
            <>
              <p className="lead">Ga naar <strong>plex.tv/link</strong> en voer deze code in:</p>
              <div className="pin-code" aria-live="polite">{pin.code}</div>
              <div className="form-actions">
                <a className="btn btn-primary" href="https://plex.tv/link" target="_blank" rel="noreferrer">plex.tv/link openen</a>
                <button className="btn btn-ghost" onClick={() => setPin(null)}>Annuleren</button>
              </div>
            </>
          ) : (
            <>
              <p className="subtle hint">Heb je (ook) een Plex-account met toegang tot deze server? Koppel het, dan zie je de bibliotheek naast je addons. Je profielen, lijst en geschiedenis blijven.</p>
              <div className="form-actions">
                <button className="btn" onClick={async () => { setMsg(null); try { setPin(await api.plexLinkPin()); } catch { setMsg('plex.tv is niet bereikbaar.'); } }}>Plex koppelen</button>
              </div>
            </>
          )}
        </>
      )}
      {msg && <p className="subtle" role="status">{msg}</p>}
    </section>
  );
}
